// Saved Carts feature — each "saved cart" is a Wishlist in the Teifi Wishlists API.
// API base: FoxTheme.wishlist.proxyPath + /api/storefront/wishlists
// Docs: https://wishlists-elem.apps.staging.teifi.dev/api/docs/storefront/wishlists
//
// - Items use Shopify GID format: gid://shopify/ProductVariant/{id}
// - The API has no add-item endpoint: addItems() GETs the wishlist, merges
//   new items client-side, then POSTs the full array back.
// - List response shape: { items: [...], page, limit, totalItems, totalPages }
// - Update uses POST (not PATCH)
// - HTML is rendered by cloning <template> elements defined in Liquid files,
//   keeping markup and i18n strings out of JS.

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const toProductGid = (id) => `gid://shopify/Product/${id}`;
const toVariantGid = (id) => `gid://shopify/ProductVariant/${id}`;
const variantIdFromGid = (gid) => Number(String(gid).split("/").pop());

// Shopify's `t` filter HTML-escapes translations whose key doesn't end in `_html`
// (so `'` becomes `&#39;`, etc). That's correct when the string lands in markup,
// but these strings are used as plain JS text (.textContent, prompt/confirm/alert),
// which never decodes entities — so decode once, here, before anything reads them.
function decodeHtmlEntities(str) {
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
}

if (FoxTheme.wishlist?.strings) {
  for (const key of Object.keys(FoxTheme.wishlist.strings)) {
    FoxTheme.wishlist.strings[key] = decodeHtmlEntities(
      FoxTheme.wishlist.strings[key],
    );
  }
}

// The wishlist API only stores productGid/variantGid/handle/quantity/note, so a line's
// customer-entered properties (e.g. personalization) can't survive a save/restore.
// Underscore-prefixed properties are private/app metadata and safe to drop.
const hasCustomProperties = (item) =>
  Object.keys(item.properties ?? {}).some((key) => !key.startsWith("_"));

// Cart line items (from /cart.js) -> writable wishlist items
const cartItemToWishlistItem = (item) => ({
  productGid: toProductGid(item.product_id),
  variantGid: toVariantGid(item.variant_id),
  handle: item.handle ?? "",
  quantity: item.quantity ?? 1,
  note: "",
});

// Extract a featured image URL from a wishlist item.
const getItemImageUrl = (item) =>
  item?.imageUrl ?? item?.image?.src ?? item?.image?.url ?? "";

// The storefront wishlists endpoint only stores productGid/variantGid/handle/quantity/note —
// no price or image. Look those up from Shopify's public product JSON (keyed by handle so
// items sharing a product only fetch it once) and graft them onto the item for display.
const productByHandle = new Map();

function fetchProductByHandle(handle) {
  if (!handle) return Promise.resolve(null);
  if (!productByHandle.has(handle)) {
    productByHandle.set(
      handle,
      fetch(`/products/${handle}.js`, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    );
  }
  return productByHandle.get(handle);
}

async function enrichWishlistItems(items) {
  const products = await Promise.all(
    items.map((item) => fetchProductByHandle(item.handle)),
  );
  return items.map((item, index) => {
    const product = products[index];
    const variantId = variantIdFromGid(item.variantGid);
    const variant =
      product?.variants?.find((v) => v.id === variantId) ??
      product?.variants?.[0];
    return {
      ...item,
      price: item.price ?? variant?.price ?? 0,
      imageUrl:
        item.imageUrl ||
        variant?.featured_image?.src ||
        product?.featured_image ||
        product?.images?.[0] ||
        "",
    };
  });
}

function cloneRow(template) {
  const node = template?.content.cloneNode(true).firstElementChild;
  return node instanceof HTMLElement ? node : null;
}

function setField(container, field, value) {
  const el = container.querySelector(`[data-field="${field}"]`);
  if (el) el.textContent = value;
  return el;
}

function messageEl(text) {
  const p = document.createElement("p");
  p.className = "py-10 text-center opacity-60";
  p.textContent = text;
  return p;
}

function setButtonLoading(btn, loading) {
  if (!btn) return;
  btn.disabled = loading;
  btn.classList.toggle("btn--loading", loading);
}

function collectSectionsToBundle() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:grouped-sections", {
      bubbles: true,
      detail: { sections },
    }),
  );
  return sections;
}

function publishCartUpdate(cartJson) {
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
    cart: cartJson,
  });
}

async function getShopifyCart() {
  const res = await fetch("/cart.js", {
    headers: { "X-Requested-With": "XMLHttpRequest" },
  });
  return res.json();
}

// /cart/add.js and /cart/clear.js responses don't carry cart-level fields like
// item_count that onCartUpdate needs (only /cart.js does) — fetch the real cart
// and graft the rendered `sections` from the mutating call onto it before publishing.
async function publishCartRefresh(sections) {
  const cartJson = await getShopifyCart();
  cartJson.sections = sections;
  publishCartUpdate(cartJson);
  return cartJson;
}

async function clearShopifyCart() {
  const res = await fetch("/cart/clear.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ sections: collectSectionsToBundle() }),
  });
  if (!res.ok) throw new Error(`Failed to clear cart (HTTP ${res.status})`);
  return res.json();
}

// Set the given cart lines (by line key) to 0 — used after saving so lines that
// weren't saved (personalized ones) stay in the cart.
// Every cart line is personalized, so there is nothing the API can store.
function alertNothingToSave() {
  window.alert(
    FoxTheme.wishlist?.strings?.nothingToSave ??
      "Personalized items can't be saved and were left in your cart.",
  );
}

async function removeShopifyCartLines(keys) {
  const res = await fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({
      updates: Object.fromEntries(keys.map((key) => [key, 0])),
      sections: collectSectionsToBundle(),
    }),
  });
  if (!res.ok)
    throw new Error(`Failed to update cart (HTTP ${res.status})`);
  return res.json();
}

async function addItemsToShopifyCart(items) {
  const payload = items
    .filter((i) => i.variantGid)
    .map((i) => ({
      id: variantIdFromGid(i.variantGid),
      quantity: i.quantity ?? 1,
    }));
  return addLinesToShopifyCart(payload);
}

async function addLinesToShopifyCart(payload) {
  if (!payload.length) return null;

  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({
      items: payload,
      sections: collectSectionsToBundle(),
    }),
  });
  if (!res.ok)
    throw new Error(`Failed to add items to cart (HTTP ${res.status})`);
  return res.json();
}

// Swap the current cart for a saved cart's items. If adding fails (sold-out variant,
// HTTP error) the previous lines are re-added so the shopper's cart isn't lost.
async function replaceShopifyCart(items) {
  const previous = await getShopifyCart();
  const clearResult = await clearShopifyCart();
  try {
    return (await addItemsToShopifyCart(items)) ?? clearResult;
  } catch (err) {
    const restored = await addLinesToShopifyCart(
      (previous.items ?? []).map((line) => ({
        id: line.variant_id,
        quantity: line.quantity,
        properties: line.properties ?? {},
        ...(line.selling_plan_allocation && {
          selling_plan: line.selling_plan_allocation.selling_plan.id,
        }),
      })),
    ).catch((restoreErr) =>
      console.error("[SavedCarts] cart restore failed:", restoreErr),
    );
    await publishCartRefresh((restored ?? clearResult)?.sections);
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// Wishlist API client
// ─────────────────────────────────────────────────────────────────
const SavedCartsAPI = {
  get base() {
    return (
      (FoxTheme.wishlist?.proxyPath ?? "/apps/wishlists-ubq") +
      "/api/storefront/wishlists"
    );
  },

  async request(path, options = {}) {
    const res = await fetch(`${this.base}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        ...options.headers,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    if (res.status === 204) return null;
    return res.json();
  },

  // GET /api/storefront/wishlists -> { items, page, limit, totalItems, totalPages }
  // listType: 'SAVED_CART' scopes this to Saved Carts only — omitting it
  // would default to 'WISHLIST' (Job Lists) and return nothing here.
  // Walks every page so carts beyond the first API page stay reachable.
  async getLists(params = {}) {
    const lists = [];
    for (let page = 1; ; page++) {
      const qs = new URLSearchParams({
        ...params,
        page,
        listType: "SAVED_CART",
      }).toString();
      const result = await this.request(`?${qs}`);
      lists.push(...(result?.items ?? []));
      if (page >= (result?.totalPages ?? 1)) return lists;
    }
  },

  // GET /api/storefront/wishlists/:id
  getList(id) {
    return this.request(`/${id}`);
  },

  // POST /api/storefront/wishlists  { name, items, listType }
  // listType is set once at creation and isn't editable via updateList().
  createList(name, items = []) {
    return this.request("", {
      method: "POST",
      body: JSON.stringify({ name, items, listType: "SAVED_CART" }),
    });
  },

  // POST /api/storefront/wishlists/:id
  updateList(id, data) {
    return this.request(`/${id}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // DELETE /api/storefront/wishlists/:id
  deleteList(id) {
    return this.request(`/${id}`, { method: "DELETE" });
  },

  // Only the writable fields are accepted by the API — price, image, etc. are server-populated.
  toWritableItem(item) {
    return {
      productGid: item.productGid,
      variantGid: item.variantGid,
      handle: item.handle ?? "",
      quantity: item.quantity ?? 1,
      note: item.note ?? "",
    };
  },

  // No dedicated add-items endpoint — GET current items, merge, then POST full array.
  async addItems(id, newItems) {
    const wishlist = await this.getList(id);
    const merged = (wishlist?.items ?? []).map(this.toWritableItem);
    for (const item of newItems.map(this.toWritableItem)) {
      const idx = merged.findIndex(
        (existing) => existing.variantGid === item.variantGid,
      );
      if (idx >= 0) {
        merged[idx] = {
          ...merged[idx],
          quantity: (merged[idx].quantity ?? 1) + (item.quantity ?? 1),
        };
      } else {
        merged.push(item);
      }
    }
    return this.updateList(id, {
      name: wishlist?.name ?? "",
      handle: wishlist?.handle ?? "",
      description: wishlist?.description ?? "",
      items: merged,
    });
  },
};

// ─────────────────────────────────────────────────────────────────
// SavedCartsList — the "Saved Carts" tab panel on the cart page
// ─────────────────────────────────────────────────────────────────
if (!customElements.get("saved-carts-list")) {
  class SavedCartsList extends HTMLElement {
    #wishlists = [];
    #boundOnClick = this.#onClick.bind(this);
    #boundOnSearch = FoxTheme.utils.debounce(
      () => this.#load({ search: this.searchInput.value }),
      300,
    );

    connectedCallback() {
      this.listEl = this.querySelector("[data-list]");
      this.rowTemplate = this.querySelector("template[data-row-template]");
      this.thumbPlaceholderTemplate = this.querySelector(
        "template[data-thumb-placeholder]",
      );
      this.searchInput = this.querySelector("[data-search-input]");

      this.addEventListener("click", this.#boundOnClick);
      this.searchInput?.addEventListener("input", this.#boundOnSearch);

      this.#load();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.#boundOnClick);
      this.searchInput?.removeEventListener("input", this.#boundOnSearch);
    }

    refresh() {
      return this.#load();
    }

    async #load(params = {}) {
      this.listEl.replaceChildren(messageEl("Loading…"));
      try {
        this.#wishlists = await SavedCartsAPI.getLists(params);
        await Promise.all(
          this.#wishlists.map(async (wishlist) => {
            wishlist.items = await enrichWishlistItems(wishlist.items ?? []);
          }),
        );
        this.#render();
      } catch (err) {
        console.error("[SavedCartsList] load failed:", err);
        this.listEl.replaceChildren(
          messageEl(FoxTheme.wishlist?.strings?.loadError ?? "Failed to load."),
        );
      }
    }

    #render() {
      this.listEl.replaceChildren();
      if (!this.#wishlists.length) {
        this.listEl.appendChild(
          messageEl(
            FoxTheme.wishlist?.strings?.noItems ?? "No saved carts yet.",
          ),
        );
        return;
      }
      for (const wishlist of this.#wishlists) {
        const row = this.#buildRow(wishlist);
        if (row) this.listEl.appendChild(row);
      }
    }

    #buildRow(wishlist) {
      const row = cloneRow(this.rowTemplate);
      if (!row) return null;

      const items = wishlist.items ?? [];
      row.dataset.wishlistId = wishlist.id;

      const nameEl = setField(row, "name", wishlist.name || "Saved Cart");
      if (nameEl) nameEl.dataset.originalName = wishlist.name || "";

      const dateEl = row.querySelector('[data-field="date"]');
      if (dateEl && wishlist.createdAt) {
        const formatted = new Date(wishlist.createdAt).toLocaleDateString();
        dateEl.textContent = (
          FoxTheme.wishlist?.strings?.createdOn ?? "Created on [date]"
        ).replace("[date]", formatted);
      }

      const imageEl = row.querySelector('[data-field="image"]');
      const firstImageUrl = getItemImageUrl(items[0]);
      if (imageEl && firstImageUrl) {
        imageEl.replaceChildren();
        const img = document.createElement("img");
        img.src = firstImageUrl;
        img.alt = "";
        img.loading = "lazy";
        img.style.cssText = "width:100%;height:100%;object-fit:cover";
        imageEl.appendChild(img);
      }

      const thumbsEl = row.querySelector('[data-field="thumbs"]');
      if (thumbsEl) {
        thumbsEl.replaceChildren();
        const visibleThumbs = 5;
        const visibleItems = items.slice(0, visibleThumbs);
        for (const item of visibleItems) {
          const span = document.createElement("span");
          span.className =
            "saved-cart-item__thumb blocks-radius-circle media-wrapper";
          const url = getItemImageUrl(item);
          if (url) {
            const img = document.createElement("img");
            img.src = url;
            img.alt = "";
            img.loading = "lazy";
            img.style.cssText = "width:100%;height:100%;object-fit:cover";
            span.appendChild(img);
          } else if (this.thumbPlaceholderTemplate) {
            span.appendChild(
              this.thumbPlaceholderTemplate.content.cloneNode(true),
            );
          }
          thumbsEl.appendChild(span);
        }
        const extra = items.length - visibleItems.length;
        if (extra > 0) {
          const more = document.createElement("span");
          more.className =
            "saved-cart-item__thumb saved-cart-item__thumb--more blocks-radius-circle flex items-center justify-center";
          more.textContent = `+${extra}`;
          thumbsEl.appendChild(more);
        }
      }

      setField(row, "item-count", items.length);

      const totalCost = items.reduce(
        (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1),
        0,
      );
      setField(
        row,
        "total-cost",
        FoxTheme.Currency.formatMoney(totalCost, FoxTheme.settings.moneyFormat),
      );

      return row;
    }

    #onClick(event) {
      const toggleBtn = event.target.closest('[data-action="toggle-search"]');
      if (toggleBtn) {
        this.#toggleSearch();
        return;
      }

      const actionEl = event.target.closest("[data-action]");
      const row = event.target.closest("[data-wishlist-id]");
      if (!actionEl || !row) return;

      const wishlistId = row.dataset.wishlistId;
      switch (actionEl.dataset.action) {
        case "rename":
          this.#startRename(
            actionEl.parentElement.querySelector('[data-field="name"]'),
          );
          break;
        case "delete":
          this.#delete(wishlistId, actionEl);
          break;
        case "view-cart":
          this.#viewCart(wishlistId, actionEl);
          break;
        case "checkout":
          this.#checkout(wishlistId, actionEl);
          break;
      }
    }

    #toggleSearch() {
      const searchWrap = this.querySelector("[data-search-wrap]");
      if (!searchWrap) return;

      searchWrap.classList.toggle("hidden");
      if (!searchWrap.classList.contains("hidden")) {
        searchWrap.querySelector("input")?.focus();
      }
    }

    #startRename(nameEl) {
      if (!nameEl) return;
      nameEl.contentEditable = "true";
      nameEl.focus();
      const range = document.createRange();
      range.selectNodeContents(nameEl);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);

      const onKeyDown = (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          nameEl.blur();
        }
        if (event.key === "Escape") {
          nameEl.textContent = nameEl.dataset.originalName;
          nameEl.blur();
        }
      };

      const save = async () => {
        nameEl.contentEditable = "false";
        nameEl.removeEventListener("keydown", onKeyDown);
        const newName = nameEl.textContent.trim();
        const origName = nameEl.dataset.originalName;
        const wishlistId =
          nameEl.closest("[data-wishlist-id]")?.dataset.wishlistId;
        if (!newName || !wishlistId || newName === origName) {
          nameEl.textContent = origName;
          return;
        }

        const current = this.#wishlists.find(
          (wishlist) => String(wishlist.id) === String(wishlistId),
        );
        try {
          await SavedCartsAPI.updateList(wishlistId, {
            name: newName,
            handle: current?.handle ?? "",
            description: current?.description ?? "",
            items: (current?.items ?? []).map(SavedCartsAPI.toWritableItem),
          });
          nameEl.dataset.originalName = newName;
          if (current) current.name = newName;
        } catch (err) {
          console.error("[SavedCartsList] rename failed:", err);
          nameEl.textContent = origName;
          window.alert(
            FoxTheme.wishlist?.strings?.renameError ?? "Failed to rename cart.",
          );
        }
      };

      nameEl.addEventListener("blur", save, { once: true });
      nameEl.addEventListener("keydown", onKeyDown);
    }

    async #delete(wishlistId, btn) {
      if (
        !window.confirm(
          FoxTheme.wishlist?.strings?.deleteConfirm ??
            "Delete this saved cart?",
        )
      )
        return;

      setButtonLoading(btn, true);
      try {
        await SavedCartsAPI.deleteList(wishlistId);
        await this.#load();
      } catch (err) {
        console.error("[SavedCartsList] delete failed:", err);
        setButtonLoading(btn, false);
      }
    }

    async #viewCart(wishlistId, btn) {
      setButtonLoading(btn, true);
      try {
        const wishlist = await SavedCartsAPI.getList(wishlistId);
        const addResult = await replaceShopifyCart(wishlist?.items ?? []);
        await SavedCartsAPI.deleteList(wishlistId);
        await publishCartRefresh(addResult?.sections);
        document.querySelector(".cart__tabs")?.setActiveTab(0);
        await this.#load();
      } catch (err) {
        console.error("[SavedCartsList] view cart failed:", err);
        window.alert(
          FoxTheme.wishlist?.strings?.loadError ?? "Failed to load.",
        );
      } finally {
        setButtonLoading(btn, false);
      }
    }

    async #checkout(wishlistId, btn) {
      setButtonLoading(btn, true);
      try {
        const wishlist = await SavedCartsAPI.getList(wishlistId);
        await replaceShopifyCart(wishlist?.items ?? []);
        await SavedCartsAPI.deleteList(wishlistId);
        window.location.href = "/checkout";
      } catch (err) {
        console.error("[SavedCartsList] checkout failed:", err);
        setButtonLoading(btn, false);
      }
    }
  }

  customElements.define("saved-carts-list", SavedCartsList);
}

// ─────────────────────────────────────────────────────────────────
// SaveCartPicker — the "Save Cart" modal (pick an existing saved cart or create new)
// ─────────────────────────────────────────────────────────────────
if (!customElements.get("save-cart-picker")) {
  class SaveCartPicker extends HTMLElement {
    #wishlists = [];
    #boundOnClick = this.#onClick.bind(this);
    #boundOnToggle = () => {
      if (this.modal.open) this.#load();
    };

    connectedCallback() {
      this.listEl = this.querySelector("[data-list]");
      this.rowTemplate = this.querySelector("template[data-row-template]");
      this.modal = this.closest("basic-modal");

      this.addEventListener("click", this.#boundOnClick);
      this.modal?.addEventListener("toggle", this.#boundOnToggle);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.#boundOnClick);
      this.modal?.removeEventListener("toggle", this.#boundOnToggle);
    }

    async #load() {
      this.listEl.replaceChildren(messageEl("Loading…"));
      try {
        this.#wishlists = await SavedCartsAPI.getLists();
        this.#render();
      } catch (err) {
        console.error("[SaveCartPicker] load failed:", err);
        this.listEl.replaceChildren(
          messageEl(FoxTheme.wishlist?.strings?.loadError ?? "Failed to load."),
        );
      }
    }

    #render() {
      this.listEl.replaceChildren();
      if (!this.#wishlists.length) {
        this.listEl.appendChild(
          messageEl(
            FoxTheme.wishlist?.strings?.noItems ?? "No saved carts yet.",
          ),
        );
        return;
      }
      for (const wishlist of this.#wishlists) {
        const row = this.#buildRow(wishlist);
        if (row) this.listEl.appendChild(row);
      }
    }

    #buildRow(wishlist) {
      const row = cloneRow(this.rowTemplate);
      if (!row) return null;

      row.dataset.wishlistId = wishlist.id;
      setField(row, "name", wishlist.name || "Saved Cart");

      const count = (wishlist.items ?? []).length;
      setField(row, "meta", `${count} ${count === 1 ? "item" : "items"}`);

      return row;
    }

    #onClick(event) {
      const createBtn = event.target.closest('[data-action="create-new"]');
      if (createBtn) {
        this.#createNew(createBtn);
        return;
      }

      const saveBtn = event.target.closest('[data-action="save-here"]');
      if (saveBtn) {
        const wishlistId =
          saveBtn.closest("[data-wishlist-id]")?.dataset.wishlistId;
        this.#saveHere(wishlistId, saveBtn);
      }
    }

    async #createNew(btn) {
      const name = window.prompt(
        FoxTheme.wishlist?.strings?.newCartPrompt ?? "Name for new saved cart:",
      );
      if (!name?.trim()) return;

      setButtonLoading(btn, true);
      try {
        const lines = await this.#getSavableLines();
        if (!lines.length) return alertNothingToSave();
        await SavedCartsAPI.createList(
          name.trim(),
          lines.map(cartItemToWishlistItem),
        );
        await this.#afterSave(lines);
      } catch (err) {
        console.error("[SaveCartPicker] create failed:", err);
        window.alert(
          FoxTheme.wishlist?.strings?.saveError ?? "Failed to save cart.",
        );
      } finally {
        setButtonLoading(btn, false);
      }
    }

    async #saveHere(wishlistId, btn) {
      if (!wishlistId) return;

      setButtonLoading(btn, true);
      try {
        const lines = await this.#getSavableLines();
        if (!lines.length) return alertNothingToSave();
        await SavedCartsAPI.addItems(
          wishlistId,
          lines.map(cartItemToWishlistItem),
        );
        await this.#afterSave(lines);
      } catch (err) {
        console.error("[SaveCartPicker] save failed:", err);
        window.alert(
          FoxTheme.wishlist?.strings?.saveError ?? "Failed to save cart.",
        );
      } finally {
        setButtonLoading(btn, false);
      }
    }

    // ponytail: personalized lines are skipped (kept in the cart) because the API
    // can't store line properties — save them too once the backend supports it.
    async #getSavableLines() {
      const cart = await getShopifyCart();
      return (cart.items ?? []).filter((line) => !hasCustomProperties(line));
    }

    async #afterSave(savedLines) {
      const result = await removeShopifyCartLines(
        savedLines.map((line) => line.key),
      );
      await publishCartRefresh(result?.sections);
      this.modal?.hide();
      document
        .querySelectorAll("saved-carts-list")
        .forEach((list) => list.refresh());
      document.querySelector(".cart__tabs")?.setActiveTab(1);
    }
  }

  customElements.define("save-cart-picker", SaveCartPicker);
}
